import { useCallback } from 'react';

import { type GasCorePayload, type GasPureScreenEventPayload } from '@atlaskit/analytics-gas-types';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

interface GasV3UIEventPayload extends GasCorePayload {
	eventType: 'ui';
}

interface GasV3TrackEventPayload extends GasCorePayload {
	eventType: 'track';
}

interface GasV3OperationalEventPayload extends GasCorePayload {
	eventType: 'operational';
}

interface GasV3ScreenEventPayload extends GasPureScreenEventPayload {
	eventType: 'screen';
}

export const usePlatformInstrumentation = (
	{ channel }: { channel?: FabricChannel } = { channel: FabricChannel.elements },
): {
	sendOperationalEvent: (payload: GasV3OperationalEventPayload) => void;
	sendScreenEvent: (payload: GasV3ScreenEventPayload) => void;
	sendTrackEvent: (payload: GasV3TrackEventPayload) => void;
	sendUIEvent: (payload: GasV3UIEventPayload) => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendUIEvent = useCallback(
		(payload: GasV3UIEventPayload): void => {
			createAnalyticsEvent(payload).fire(channel);
		},
		[channel, createAnalyticsEvent],
	);

	const sendTrackEvent = useCallback(
		(payload: GasV3TrackEventPayload): void => {
			createAnalyticsEvent(payload).fire(channel);
		},
		[channel, createAnalyticsEvent],
	);

	const sendOperationalEvent = useCallback(
		(payload: GasV3OperationalEventPayload): void => {
			createAnalyticsEvent(payload).fire(channel);
		},
		[channel, createAnalyticsEvent],
	);

	const sendScreenEvent = useCallback(
		(payload: GasV3ScreenEventPayload): void => {
			createAnalyticsEvent(payload).fire(channel);
		},
		[channel, createAnalyticsEvent],
	);

	return {
		sendUIEvent,
		sendTrackEvent,
		sendOperationalEvent,
		sendScreenEvent,
	};
};
